import { Loader, Modal, Text } from "@marketplace/ui";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { MdErrorOutline } from "react-icons/md";

import { useGetPayoutStatusQuery } from "../../hooks/queries/use-get-payout-status-query";
import { PayoutStatusPill } from "./payout-status-pill";

type PayoutStatusModalProps = {
  opened: boolean;
  onClose: () => void;
  orderId: string | null;
};

const PAYOUT_TYPE_LABELS: Record<string, string> = {
  internal: "Internal",
  external: "External",
  pph23: "PPH23"
};

export const PayoutStatusModal: FC<PayoutStatusModalProps> = ({
  opened,
  onClose,
  orderId
}) => {
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage.payoutStatusModal"
  });

  const { data, isLoading, isError } = useGetPayoutStatusQuery({
    queryParams: { orderId: orderId ?? "" },
    options: {
      enabled: opened && !!orderId,
      retry: false
    }
  });

  const steps = data?.data?.steps ?? [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text type="heading">{t("title")}</Text>}
      classNames={{
        header: "!p-6 !min-h-fit",
        body: "!p-6 !pt-0"
      }}
      size={540}
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2 text-fjord-grey-400">
          <MdErrorOutline size={48} />
          <Text type="heading" size="xxs">
            {t("notFound")}
          </Text>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {steps.map((step, index) => {
            return (
              <div
                key={step.payout_type}
                className="flex flex-col border border-solid rounded-lg p-2 gap-3 border-mischka-400"
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-2 text-fjord-grey-500">
                    <Text size="l">{t("step", { number: index + 1 })}</Text>
                    <Text type="heading" size="xs" weight="bold">
                      {PAYOUT_TYPE_LABELS[step.payout_type] || step.payout_type}
                    </Text>
                  </div>
                  <PayoutStatusPill status={step.payout_status} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
